import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { isReviewPilot } from "@/lib/featureFlags";
import { overviewFixture } from "@/lib/overviewFixture";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { hasDirectSupport } from "@/lib/profileModules";
import { completenessItems, nextAdminTask, percent, STAGE_LABEL, STAGES, type Stage } from "@/lib/athleteHub";

type Row = {
  athlete_id: string; stage: Stage; quick_start_submitted_at: string | null; module_choices: any;
  changes_requested: string | null; approved_at: string | null; approved_current_version: boolean;
  consent_profile: boolean; consent_likeness: boolean; follower_count: number; open_inquiries: number;
  claim_link_status: string | null;
};

/** Admin-only roster view: lifecycle, completeness, approval, modules, followers, opportunities, next task. */
const AdminAthleteOverview = () => {
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]);
  const [athletes, setAthletes] = useState<Record<string, any>>({});
  const [teams, setTeams] = useState<Record<string, string[]>>({});
  const [shops, setShops] = useState<Record<string, string>>({});
  const [causeCounts, setCauseCounts] = useState<Record<string, number>>({});
  const [stageFilter, setStageFilter] = useState<string>("all");
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [link, setLink] = useState<{ name: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [o, a, at, cs, c] = await Promise.all([
      supabase.rpc("admin_athlete_overview"),
      supabase.from("athletes").select("*").order("name"),
      supabase.from("athlete_teams").select("athlete_id, teams(name)"),
      supabase.from("athlete_commerce_settings").select("athlete_id, shop_status, is_test"),
      supabase.from("causes").select("athlete_id").eq("published", true),
    ]);
    if (o.error) return toast.error(o.error.message);
    // Admin visual QA: ?fixture=N pads the list with in-memory sample athletes (never saved).
    const fx = overviewFixture(Math.max(0, Number(searchParams.get("fixture") ?? 0) - (o.data ?? []).length));
    setRows([...((o.data ?? []) as Row[]), ...(fx.rows as Row[])]);
    setAthletes({ ...Object.fromEntries((a.data ?? []).map((x: any) => [x.id, x])), ...fx.athletes });
    const t: Record<string, string[]> = { ...fx.teams };
    for (const r of (at.data ?? []) as any[]) (t[r.athlete_id] ??= []).push(r.teams?.name ?? "");
    setTeams(t);
    setShops(Object.fromEntries((cs.data ?? []).map((x: any) => [x.athlete_id, x.is_test ? `${x.shop_status} (test)` : x.shop_status])));
    const cc: Record<string, number> = {};
    for (const r of (c.data ?? []) as any[]) if (r.athlete_id) cc[r.athlete_id] = (cc[r.athlete_id] ?? 0) + 1;
    setCauseCounts(cc);
  };
  useEffect(() => { load(); }, []);

  const allTeams = useMemo(() => [...new Set(Object.values(teams).flat())].filter(Boolean).sort(), [teams]);

  const issue = async (id: string) => {
    if (id.startsWith("fx-")) return toast.info("Sample athlete — nothing to change.");
    setBusy(true);
    const { data, error } = await supabase.rpc("admin_issue_claim_link", { _athlete_id: id, _days: 14 });
    setBusy(false);
    if (error) return toast.error(error.message);
    setLink({ name: athletes[id]?.name, url: `${window.location.origin}/claim?t=${data}` });
    load();
  };
  const step = async (id: string, stage: "athlete_approval" | "siis_review") => {
    if (id.startsWith("fx-")) return toast.info("Sample athlete — nothing to change.");
    const { error } = await supabase.rpc("admin_set_athlete_stage", { _athlete_id: id, _stage: stage, _note: null as any });
    if (error) return toast.error(error.message);
    toast.success(stage === "athlete_approval" ? "Sent to athlete for approval" : "Returned to SIIS review");
    load();
  };
  const override = async (id: string, name: string) => {
    if (id.startsWith("fx-")) return toast.info("Sample athlete — nothing to change.");
    const stage = window.prompt(`Admin override for ${name}.\nEnter stage: invited, started, siis_review or athlete_approval`);
    if (!stage) return;
    const reason = window.prompt("Reason for this override (required, recorded in the audit log)");
    if (!reason) return;
    const { data, error } = await supabase.rpc("admin_override_athlete_stage", { _athlete_id: id, _stage: stage.trim(), _reason: reason });
    if (error) return toast.error(error.message);
    toast.success(`Override recorded — now ${STAGE_LABEL[data as Stage] ?? data}`);
    load();
  };

  const [query, setQuery] = useState("");
  const [needsOnly, setNeedsOnly] = useState(false);
  const [open, setOpen] = useState<string | null>(null);

  const enriched = rows
    .filter((r) => athletes[r.athlete_id])
    .map((r) => {
      const a = athletes[r.athlete_id];
      const pct = percent(completenessItems(a, r.module_choices ?? {}, causeCounts[r.athlete_id] ?? 0));
      const h = { ...r, consents: { profile_publication: r.consent_profile, photo_likeness: r.consent_likeness }, published: a.published };
      const next = nextAdminTask(h as any, pct, Boolean(a.owner_user_id));
      return { r, a, pct, next, siisAction: next.startsWith("SIIS") || next === "Send claim link" || next === "Publish when ready" || next.startsWith("Live profile differs") };
    });
  const counts = Object.fromEntries(STAGES.map((s) => [s, enriched.filter((e) => e.r.stage === s).length]));
  const view = enriched
    .filter((e) => stageFilter === "all" || e.r.stage === stageFilter)
    .filter((e) => teamFilter === "all" || (teams[e.r.athlete_id] ?? []).includes(teamFilter))
    .filter((e) => !needsOnly || e.siisAction)
    .filter((e) => !query.trim() || e.a.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((x, y) => Number(y.siisAction) - Number(x.siisAction) || x.a.name.localeCompare(y.a.name));

  const pill = "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap";
  const approvalLabel = (r: Row) => r.approved_current_version ? "Approved" : r.approved_at ? "Changed since approval" : r.changes_requested ? "Changes requested" : "Not yet";
  return (
    <AdminLayout>
      <div className="space-y-5">
        <div>
          <h1 className="font-display text-3xl">Athlete Overview</h1>
          <p className="mt-1 text-sm text-muted-foreground">Rows needing SIIS action are listed first. Open a row for claim, consent and lifecycle controls. Publishing stays on the athlete editor's publish switch.</p>
        </div>

        {link && (
          <div role="status" className="rounded-lg border border-primary-strong bg-card p-4 text-sm">
            <p className="font-semibold">Claim link for {link.name} — shown once, single use, expires in 14 days</p>
            <div className="mt-2 flex flex-wrap gap-2"><input readOnly value={link.url} className="min-w-0 flex-1 rounded border border-input bg-background px-2 py-1 text-xs" aria-label="Claim link" />
              <Button size="sm" onClick={() => { navigator.clipboard.writeText(link.url); toast.success("Copied"); }}>Copy</Button>
              <Button size="sm" variant="outline" onClick={() => setLink(null)}>Done</Button></div>
          </div>
        )}

        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter by stage">
          {(["all", ...STAGES] as const).map((s) => (
            <button key={s} type="button" aria-pressed={stageFilter === s} onClick={() => setStageFilter(s)}
              className={`min-h-[36px] rounded-full border px-3 text-xs font-semibold ${stageFilter === s ? "border-primary-strong bg-primary-strong text-primary-foreground" : "border-border bg-card hover:bg-muted"}`}>
              {s === "all" ? `All · ${enriched.length}` : `${STAGE_LABEL[s]} · ${counts[s]}`}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search athletes" aria-label="Search athletes" className="min-h-[36px] w-full sm:w-56 rounded-md border border-input bg-background px-3" />
          <select aria-label="Filter by team" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className="min-h-[36px] rounded-md border border-input bg-background px-2">
            <option value="all">All teams</option>{allTeams.map((t) => <option key={t}>{t}</option>)}
          </select>
          <label className="inline-flex min-h-[36px] items-center gap-2"><input type="checkbox" checked={needsOnly} onChange={(e) => setNeedsOnly(e.target.checked)} /> Needs SIIS action</label>
          <span className="text-muted-foreground">{view.length} shown</span>
        </div>

        <div className="hidden lg:grid grid-cols-[minmax(0,2.2fr)_8rem_7rem_8rem_minmax(0,2fr)_4.5rem_4.5rem_5rem_6rem] gap-3 border-b border-border px-3 pb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span>Athlete / team</span><span>Stage</span><span>Profile</span><span>Approval</span><span>Next task</span><span>Fans</span><span>Inquiries</span><span>Direct</span><span>Collection</span>
        </div>
        <ul className="space-y-2 lg:space-y-0 lg:divide-y lg:divide-border">
          {view.map(({ r, a, pct, next, siisAction }) => {
            const isOpen = open === r.athlete_id;
            const ds = hasDirectSupport(a).active ? "On" : r.module_choices?.direct_support ? "Wanted" : "Off";
            const hc = shops[r.athlete_id] ?? (r.module_choices?.her_collection === "interested" ? "Interested" : "Off");
            return (
              <li key={r.athlete_id} className="rounded-xl border border-border bg-card lg:rounded-none lg:border-0 lg:bg-transparent">
                <button type="button" aria-expanded={isOpen} onClick={() => setOpen(isOpen ? null : r.athlete_id)}
                  className="w-full text-left p-3 grid gap-2 grid-cols-2 lg:grid-cols-[minmax(0,2.2fr)_8rem_7rem_8rem_minmax(0,2fr)_4.5rem_4.5rem_5rem_6rem] lg:gap-3 lg:items-center hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-xl lg:rounded-none">
                  <span className="col-span-2 lg:col-span-1 min-w-0">
                    <span className="block font-semibold truncate">{a.name}</span>
                    <span className="block text-xs text-muted-foreground truncate">{(teams[r.athlete_id] ?? []).join(", ") || "No team"}</span>
                  </span>
                  <span><span className={`${pill} ${r.stage === "published" ? "bg-primary-strong text-primary-foreground" : r.stage === "ready" ? "bg-primary/15 text-primary-strong" : "bg-muted"}`}>{STAGE_LABEL[r.stage] ?? r.stage}</span></span>
                  <span className="flex items-center gap-2">
                    <span className="h-1.5 w-12 overflow-hidden rounded-full bg-muted" aria-hidden><span className="block h-full bg-primary-strong" style={{ width: `${pct}%` }} /></span>
                    <span className="text-xs tabular-nums">{pct}%</span>
                  </span>
                  <span className="text-xs">{approvalLabel(r)}</span>
                  <span className={`col-span-2 lg:col-span-1 text-xs ${siisAction ? "font-semibold text-foreground" : "text-muted-foreground"}`}>{siisAction && <span aria-hidden className="mr-1 inline-block h-2 w-2 rounded-full bg-primary-strong" />}{next}</span>
                  <span className="text-xs tabular-nums"><span className="lg:hidden text-muted-foreground">Fans </span>{r.follower_count}</span>
                  <span className="text-xs tabular-nums"><span className="lg:hidden text-muted-foreground">Inquiries </span>{r.open_inquiries}</span>
                  <span className="text-xs"><span className="lg:hidden text-muted-foreground">Direct </span>{ds}</span>
                  <span className="text-xs truncate"><span className="lg:hidden text-muted-foreground">Collection </span>{hc}</span>
                </button>
                {isOpen && (
                  <div className="border-t border-border px-3 py-3 text-sm grid gap-3 sm:grid-cols-2 lg:grid-cols-4 bg-muted/30">
                    <div><p className="text-xs text-muted-foreground">Claim</p><p>{a.owner_user_id ? "Claimed" : r.claim_link_status ? `Link ${r.claim_link_status}` : "Unclaimed"}</p>
                      {!a.owner_user_id && <Button className="mt-1" size="sm" variant="outline" disabled={busy} onClick={() => issue(r.athlete_id)}>{r.claim_link_status === "active" ? "New claim link" : "Claim link"}</Button>}</div>
                    <div><p className="text-xs text-muted-foreground">Consent</p><p>{r.consent_profile && r.consent_likeness ? "Both given" : r.consent_profile || r.consent_likeness ? "Partial" : "None"}</p>
                      <p className="text-xs text-muted-foreground mt-1">Quick Start: {r.quick_start_submitted_at ? "Submitted" : "Not yet"} · Published: {a.published ? "Yes" : "No"}</p></div>
                    <div><p className="text-xs text-muted-foreground">Lifecycle</p>
                      {!a.published ? (
                        <div className="mt-1 flex flex-col items-start gap-1 text-xs">
                          {r.stage === "siis_review" && <button className="font-semibold text-primary-strong min-h-[32px]" onClick={() => step(r.athlete_id, "athlete_approval")}>Send for approval</button>}
                          {r.stage === "athlete_approval" && <button className="font-semibold text-primary-strong min-h-[32px]" onClick={() => step(r.athlete_id, "siis_review")}>Back to SIIS review</button>}
                          <button className="text-muted-foreground underline min-h-[32px]" onClick={() => override(r.athlete_id, a.name)}>Override…</button>
                        </div>) : <p className="text-xs text-muted-foreground">Live — unpublish from the editor.</p>}
                      {r.changes_requested && <p className="mt-1 text-xs">Athlete note: “{r.changes_requested}”</p>}
                      {isReviewPilot(r.athlete_id) && <Link to={`/admin/athletes/${r.athlete_id}/draft`} className="mt-1 block text-xs font-semibold text-primary-strong">Profile draft (pilot) →</Link>}</div>
                    <div className="flex flex-col items-start gap-1 text-xs">
                      <a className="font-semibold text-primary-strong min-h-[32px] inline-flex items-center" href={`/athlete/${a.slug}?adminPreview=1`} target="_blank" rel="noreferrer">Admin preview ↗</a>
                      <a className="font-semibold text-primary-strong min-h-[32px] inline-flex items-center" href={`/athlete/${a.slug}?adminPreview=1&layout=hub`} target="_blank" rel="noreferrer">Business Hub preview ↗</a>
                    </div>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </AdminLayout>
  );
};

export default AdminAthleteOverview;
